import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { api, money } from "@/lib/api";

const short = (m) => new Date(`${m}-01T00:00:00`).toLocaleString("en-IN", { month: "short", year: "2-digit" });

/** Month-by-month package-fee collections for the registry owner. */
export const LedgerTrend = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/registry/ledger/monthly").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) return <div className="h-56 border border-border bg-muted animate-pulse mt-8" />;

  const max = Math.max(...data.months.map((m) => m.collected), 1);
  const change = data.summary.change_vs_last_month_pct;

  return (
    <section className="mt-8 border border-border bg-card p-6" data-testid="ledger-trend">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-caps">Collections · last 12 months</p>
          <p className="text-sm text-muted-foreground mt-2">
            Package fees received by the registry, month by month, by date of payment.
          </p>
        </div>
        <dl className="flex gap-8 text-sm">
          <div>
            <dt className="label-caps">Monthly average</dt>
            <dd className="font-display text-xl mt-1" data-testid="trend-average">{money(data.summary.average)}</dd>
          </div>
          <div>
            <dt className="label-caps">vs last month</dt>
            <dd className={`font-display text-xl mt-1 flex items-center gap-1 ${change == null ? "" : change >= 0 ? "text-green-800" : "text-red-800"}`}
              data-testid="trend-change">
              {change == null ? "—" : <>{change >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}{Math.abs(change)}%</>}
            </dd>
          </div>
        </dl>
      </div>

      <div className="mt-6 grid grid-cols-12 gap-1.5 items-end h-40" data-testid="trend-bars">
        {data.months.map((m) => (
          <div key={m.month} className="flex flex-col items-center justify-end h-full group" data-testid={`trend-bar-${m.month}`}
            title={`${m.label}: ${money(m.collected)} collected · ${m.paid_notes} fee note(s)`}>
            <span className="text-[10px] font-mono text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 mb-1 whitespace-nowrap">
              {m.collected ? money(m.collected) : ""}
            </span>
            <div className={`w-full transition-colors duration-200 ${m.month === data.summary.best_month ? "bg-blue-900" : "bg-slate-700 group-hover:bg-blue-800"}`}
              style={{ height: `${Math.max((m.collected / max) * 100, m.collected ? 4 : 1)}%` }} />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2">{short(m.month)}</span>
          </div>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {["Month", "Collected", "Fee notes settled", "Raised"].map((h) => (
                <th key={h} className="py-2 pr-4 text-left label-caps whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="trend-rows">
            {[...data.months].reverse().map((m) => (
              <tr key={m.month} className="border-b border-border last:border-0" data-testid={`trend-row-${m.month}`}>
                <td className="py-2 pr-4 whitespace-nowrap">{m.label}</td>
                <td className="py-2 pr-4 font-mono">{money(m.collected)}</td>
                <td className="py-2 pr-4 font-mono">{m.paid_notes}</td>
                <td className="py-2 pr-4 font-mono text-muted-foreground">{money(m.raised)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
